import * as Yup from "yup";
import { isReservedPortalName } from "@/features/portal/portal-base";
import { PHONE_RE, ZIP_RE } from "@/lib/masks";

// Each data step owns a small, independent form. Validation is intentionally
// lenient: every field is optional (the whole step is skippable), so rules only
// bite on values the user actually typed — a blank step always validates.

// --- Step 1: firm contact & address ---------------------------------------
export interface FirmContactValues {
  email: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
}

export const firmContactSchema: Yup.ObjectSchema<FirmContactValues> = Yup.object({
  email: Yup.string().trim().email("Enter a valid email.").default(""),
  phone: Yup.string()
    .matches(PHONE_RE, { message: "Use (123) 456-7890.", excludeEmptyString: true })
    .default(""),
  addressLine1: Yup.string().trim().max(120).default(""),
  addressLine2: Yup.string().trim().max(120).default(""),
  city: Yup.string().trim().max(60).default(""),
  state: Yup.string()
    .matches(/^[A-Za-z]{2}$/, { message: "Two-letter state.", excludeEmptyString: true })
    .default(""),
  zipCode: Yup.string()
    .matches(ZIP_RE, { message: "Use 12345 or 12345-6789.", excludeEmptyString: true })
    .default(""),
});

// --- Step 4: client portal -------------------------------------------------
export interface PortalTeamValues {
  portalEnabled: boolean;
  portalName: string;
}

export const portalTeamSchema: Yup.ObjectSchema<PortalTeamValues> = Yup.object({
  portalEnabled: Yup.boolean().default(false),
  // The portal handle: the path segment in ``portal.<host>/<handle>`` —
  // letters/numbers, must start with a letter, required only once the portal is
  // switched on.
  portalName: Yup.string()
    .trim()
    .default("")
    .when("portalEnabled", {
      is: true,
      then: (s) =>
        s
          .required("Choose a portal name.")
          .matches(/^[a-zA-Z][a-zA-Z0-9]*$/, "Letters and numbers; start with a letter.")
          .test(
            "not-reserved",
            "That portal name is reserved — please choose another.",
            (v) => !v || !isReservedPortalName(v),
          ),
    }),
});

export const portalTeamInitial: PortalTeamValues = {
  portalEnabled: false,
  portalName: "",
};
